import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  WarningCircle,
  ChatCircleDots,
  NotePencil,
  Check,
  X,
  Trash,
  ArrowRight,
  IdentificationBadge,
  CircleNotch,
  Factory,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { AdminHeader } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";

const TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
];

const SOURCE_META = {
  form: { label: "Reported via form", Icon: NotePencil, cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  chat: { label: "Raised in chat with Nick", Icon: ChatCircleDots, cls: "text-[#73bf44] bg-[#73bf44]/10 border-[#73bf44]/30" },
  production_sync: { label: "Production schedule sync", Icon: Factory, cls: "text-violet-300 bg-violet-500/10 border-violet-500/30" },
};

const SOURCES = [
  { key: "", label: "All sources" },
  { key: "customer", label: "Customers" },
  { key: "production_sync", label: "Production Sync" },
];

const STATUS_CLS = {
  open: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  resolved: "text-[#73bf44] bg-[#73bf44]/10 border-[#73bf44]/30",
  dismissed: "text-neutral-500 bg-neutral-800/60 border-neutral-700",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function CorrectionRow({ c, disabled, onApply }) {
  const [value, setValue] = useState(c.suggested_value || "");
  return (
    <div className="bg-[#141414] border border-neutral-800 rounded-xl p-3.5" data-testid={`correction-${c.field_key}`}>
      <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-neutral-500 mb-2">{c.field_label}</div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="line-through text-neutral-500 break-all">{c.current_value || "—"}</span>
        <ArrowRight size={14} className="text-neutral-600 shrink-0" />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          data-testid={`correction-input-${c.field_key}`}
          className="flex-1 min-w-[140px] bg-black/50 border border-neutral-700 rounded-lg px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors disabled:opacity-60"
        />
        <button
          onClick={() => onApply(c.field_key, value)}
          disabled={disabled || !value.trim()}
          data-testid={`correction-apply-${c.field_key}`}
          className="inline-flex items-center gap-1.5 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-3.5 py-1.5 text-xs hover:bg-[#5fa338] transition-colors disabled:opacity-50"
        >
          <Check size={13} weight="bold" /> Apply
        </button>
      </div>
    </div>
  );
}

function DiscrepancyCard({ d, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const meta = SOURCE_META[d.source] || SOURCE_META.form;
  const Icon = meta.Icon;
  const applied = new Set(d.applied_fields || []);

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged();
      window.dispatchEvent(new Event("otm:badges-refresh"));
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const applyOne = (field_key, value) =>
    call(
      () => adminApi.post(`/admin/discrepancies/${d.id}/apply`, { corrections: { [field_key]: value }, resolve: false }),
      "Caravan record updated"
    );

  const applyAllResolve = () => {
    const corrections = {};
    (d.corrections || []).forEach((c) => {
      if ((c.suggested_value || "").trim()) corrections[c.field_key] = c.suggested_value.trim();
    });
    call(
      () => adminApi.post(`/admin/discrepancies/${d.id}/apply`, { corrections, resolve: true }),
      "Applied all corrections & resolved"
    );
  };

  const setStatus = (status, msg) =>
    call(() => adminApi.post(`/admin/discrepancies/${d.id}/status`, { status }), msg);

  const del = () => {
    if (!window.confirm("Delete this report permanently?")) return;
    call(() => adminApi.delete(`/admin/discrepancies/${d.id}`), "Deleted");
  };

  const snapshot = Object.entries(d.snapshot || {}).filter(([, v]) => v);

  return (
    <div data-testid="discrepancy-card" className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`}>
            <Icon size={13} weight="fill" /> {meta.label}
          </span>
          <span className={`text-[0.65rem] font-semibold px-2.5 py-1 rounded-full border capitalize ${STATUS_CLS[d.status] || STATUS_CLS.open}`}>
            {d.status}
          </span>
          {d.applied && (
            <span className="text-[0.65rem] font-semibold px-2.5 py-1 rounded-full border text-[#73bf44] border-[#73bf44]/30 bg-[#73bf44]/10">
              Correction applied
            </span>
          )}
        </div>
        <span className="text-[0.65rem] text-neutral-600">{fmt(d.created_at)}</span>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span className="inline-flex items-center gap-1.5 font-mono text-sm tracking-widest text-[#73bf44]">
          <IdentificationBadge size={16} weight="duotone" /> {d.chassis_number}
        </span>
        {d.model && <span className="text-sm text-neutral-300 font-heading font-semibold uppercase">{d.model}</span>}
        {!d.caravan_found && (
          <span className="text-[0.65rem] text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-full px-2 py-0.5">
            No matching caravan on file
          </span>
        )}
      </div>

      {(d.corrections || []).length > 0 && (
        <div className="space-y-2.5 mb-4" data-testid="discrepancy-corrections">
          {d.corrections.map((c) => (
            <CorrectionRow
              key={c.field_key}
              c={c}
              disabled={busy || applied.has(c.field_key)}
              onApply={applyOne}
            />
          ))}
        </div>
      )}

      {(d.summary || d.note) && (
        <div className="mb-4">
          {d.summary && <div className="text-sm text-neutral-300 font-medium mb-1">{d.summary}</div>}
          {d.note && (
            <div className="text-sm text-neutral-400 border-l-2 border-neutral-800 pl-3 whitespace-pre-line" data-testid="discrepancy-note">
              {d.note}
            </div>
          )}
        </div>
      )}

      {snapshot.length > 0 && (
        <details className="mb-4 group">
          <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-300 select-none">
            Configuration on file at time of report
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2">
            {snapshot.map(([k, v]) => (
              <div key={k} className="text-xs bg-[#141414] border border-neutral-800 rounded-lg px-3 py-2">
                <span className="text-neutral-500 capitalize">{k.replace(/_/g, " ")}: </span>
                <span className="text-neutral-200">{String(v)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-4 border-t border-neutral-800">
        {(d.corrections || []).length > 0 && d.status === "open" && (
          <button
            onClick={applyAllResolve}
            disabled={busy}
            data-testid="discrepancy-apply-all"
            className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-4 py-2 text-sm hover:bg-[#5fa338] transition-colors disabled:opacity-60"
          >
            <Check size={15} weight="bold" /> Apply all & resolve
          </button>
        )}
        <button
          onClick={() => navigate(`/admin/caravans?search=${encodeURIComponent(d.chassis_number)}`)}
          disabled={busy}
          data-testid="discrepancy-edit-record"
          className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-200 rounded-full px-4 py-2 text-sm hover:border-neutral-500 hover:text-white transition-colors"
        >
          <NotePencil size={15} /> Edit caravan record
        </button>
        {d.status !== "resolved" && (
          <button
            onClick={() => setStatus("resolved", "Marked resolved")}
            disabled={busy}
            data-testid="discrepancy-resolve"
            className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-200 rounded-full px-4 py-2 text-sm hover:border-[#73bf44]/60 hover:text-[#73bf44] transition-colors"
          >
            <Check size={15} /> Mark resolved
          </button>
        )}
        {d.status !== "dismissed" && (
          <button
            onClick={() => setStatus("dismissed", "Dismissed")}
            disabled={busy}
            data-testid="discrepancy-dismiss"
            className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-400 rounded-full px-4 py-2 text-sm hover:text-white transition-colors"
          >
            <X size={15} /> Dismiss
          </button>
        )}
        <button
          onClick={del}
          disabled={busy}
          data-testid="discrepancy-delete"
          className="ml-auto p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <Trash size={16} />
        </button>
      </div>
    </div>
  );
}

export default function AdminDiscrepancies() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [source, setSource] = useState("");
  const [openCount, setOpenCount] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    adminApi
      .get("/admin/discrepancies", { params: { status: tab, ...(source ? { source } : {}) } })
      .then(({ data }) => setRows(data))
      .catch((e) => {
        setRows([]);
        toast.error(getErrorMessage(e, "Couldn't load reports. Please retry."));
      })
      .finally(() => setLoading(false));
    adminApi.get("/admin/discrepancies/count").then(({ data }) => setOpenCount(data.open || 0)).catch(() => {});
  }, [tab, source]);

  useEffect(() => {
    load();
  }, [load]);

  const dismissAllSync = async () => {
    if (!window.confirm("Dismiss ALL open Production Sync flags? They won't be raised again unless the schedule changes.")) return;
    try {
      const { data } = await adminApi.post("/admin/discrepancies/bulk-status", { source: "production_sync", status: "dismissed" });
      toast.success(`Dismissed ${data.updated} flag(s)`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div>
      <AdminHeader
        title="Config Discrepancies"
        subtitle="Customer reports and production-schedule differences — review and apply fixes to the caravan record."
        actions={
          source === "production_sync" && tab === "open" && rows.length > 0 ? (
            <button
              onClick={dismissAllSync}
              data-testid="discrepancies-dismiss-all-sync"
              className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-300 rounded-full px-4 py-2.5 text-sm hover:text-white hover:border-neutral-500 transition-colors"
            >
              <X size={15} /> Dismiss all sync flags
            </button>
          ) : null
        }
      />
      <div className="p-8">
        <div className="flex gap-2 mb-3 flex-wrap" data-testid="discrepancies-source-filter">
          {SOURCES.map((s) => (
            <button
              key={s.key || "all"}
              onClick={() => setSource(s.key)}
              data-testid={`discrepancies-source-${s.key || "all"}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                source === s.key ? "bg-white/10 text-white border border-neutral-600" : "border border-neutral-800 text-neutral-500 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`discrepancies-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                tab === t.key ? "bg-[#73bf44] text-[#050505]" : "border border-neutral-800 text-neutral-400 hover:text-white"
              }`}
            >
              {t.label}
              {t.key === "open" && openCount > 0 && (
                <span className={`text-[0.6rem] font-bold min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center ${tab === t.key ? "bg-[#050505] text-[#73bf44]" : "bg-red-500 text-white"}`}>
                  {openCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500">
            <CircleNotch size={18} className="animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3" data-testid="discrepancies-empty">
            <WarningCircle size={36} className="text-neutral-700" weight="duotone" />
            No {tab === "all" ? "" : tab} discrepancy reports.
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2" data-testid="discrepancies-list">
            {rows.map((d) => (
              <DiscrepancyCard key={d.id} d={d} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
